// ===== CONSTANTS =====
const CALENDAR_CONSTANTS = {
	// Base dimensions
	HOUR_HEIGHT: 160,
	HOUR_WIDTH: 240,

	// Set duration thresholds (in minutes)
	SHORT_SET_THRESHOLDS: [10, 15, 20, 25, 30] as const,

	// Minimum dimensions for readability
	MIN_SET_HEIGHT: 24,
	MIN_SET_WIDTH: 24,
	MIN_NORMAL_SET_HEIGHT: 80,
	MIN_NORMAL_SET_WIDTH: 132,
} as const;

// ===== TYPES =====
export type SetSizeCategory =
	| "10m-short"
	| "15m-short"
	| "20m-short"
	| "25m-short"
	| "30m-short"
	| "normal";

interface SetDimensions {
	height: number;
	width: number;
	category: SetSizeCategory;
}

interface CalendarDimensions {
	hourHeight: number;
	hourWidth: number;
	minSetHeight: number;
	minSetWidth: number;
}

// ===== SET SIZE CALCULATIONS =====

/**
 * Determines the size category for a set based on its duration in hours
 */
export function getSetSizeCategory(durationHours: number): SetSizeCategory {
	const durationMinutes = durationHours * 60;

	for (const threshold of CALENDAR_CONSTANTS.SHORT_SET_THRESHOLDS) {
		if (durationMinutes <= threshold) {
			return `${threshold}m-short` as SetSizeCategory;
		}
	}
	return "normal";
}

/**
 * Calculates dimensions for a set based on its duration
 * Uses proportional scaling with minimum thresholds for readability
 */
export function getSetDimensions(durationHours: number): SetDimensions {
	const category = getSetSizeCategory(durationHours);
	const durationMinutes = durationHours * 60;

	if (category === "normal") {
		return {
			height: Math.max(
				CALENDAR_CONSTANTS.MIN_NORMAL_SET_HEIGHT,
				durationHours * CALENDAR_CONSTANTS.HOUR_HEIGHT,
			),
			width: Math.max(
				CALENDAR_CONSTANTS.MIN_NORMAL_SET_WIDTH,
				durationHours * CALENDAR_CONSTANTS.HOUR_WIDTH,
			),
			category,
		};
	}

	// For short sets: use proportional scaling with minimum thresholds
	const proportionalHeight = Math.round(
		(durationMinutes / 60) * CALENDAR_CONSTANTS.HOUR_HEIGHT,
	);
	const proportionalWidth = Math.round(
		(durationMinutes / 60) * CALENDAR_CONSTANTS.HOUR_WIDTH,
	);

	return {
		height: Math.max(CALENDAR_CONSTANTS.MIN_SET_HEIGHT, proportionalHeight),
		width: Math.max(CALENDAR_CONSTANTS.MIN_SET_WIDTH, proportionalWidth),
		category,
	};
}

// ===== STAGE LAYOUT CALCULATIONS =====

/**
 * Calculates the optimal stage column width based on the number of stages (for vertical calendar)
 */
export function getStageColumnWidth(stageCount: number): number {
	if (stageCount === 1) return 216;
	if (stageCount === 2) return 208;
	if (stageCount === 3) return 200;
	if (stageCount === 4) return 192;
	if (stageCount === 5) return 184;
	return 176; // 6+ stages
}

/**
 * Calculates the optimal stage row height based on the number of stages (for horizontal calendar)
 */
export function getStageRowHeight(stageCount: number): number {
	if (stageCount === 1) return 200;
	if (stageCount === 2) return 192;
	if (stageCount === 3) return 184;
	if (stageCount === 4) return 176;
	if (stageCount === 5) return 168;
	return 160; // 6+ stages
}

// ===== DISPLAY LOGIC =====

/**
 * Determines whether to show text or icon for a short set based on size
 */
export function shouldShowTextForShortSet(
	sizeCategory: SetSizeCategory,
): boolean {
	// Only 25m+ sets have enough space for text
	return (
		sizeCategory === "25m-short" ||
		sizeCategory === "30m-short" ||
		sizeCategory === "normal"
	);
}

/**
 * Determines whether to show time label for short sets with enough space
 */
export function shouldShowTimeForShortSet(
	sizeCategory: SetSizeCategory,
): boolean {
	// Only 25m+ sets have enough space for time labels
	return (
		sizeCategory === "25m-short" ||
		sizeCategory === "30m-short" ||
		sizeCategory === "normal"
	);
}

// ===== CALENDAR CONFIGURATION =====

/**
 * Returns standard calendar dimensions for all festivals
 */
export function calculateOptimalCalendarDimensions(): CalendarDimensions {
	return {
		hourHeight: CALENDAR_CONSTANTS.HOUR_HEIGHT,
		hourWidth: CALENDAR_CONSTANTS.HOUR_WIDTH,
		minSetHeight: CALENDAR_CONSTANTS.MIN_NORMAL_SET_HEIGHT,
		minSetWidth: CALENDAR_CONSTANTS.MIN_NORMAL_SET_WIDTH,
	};
}
